"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { SortType, SortDirection } from "@/components/Dashboard/sort/sort";
import { GaugeData, GaugeConfig } from "@/lib/types/gauge";

export type ActiveRefDateMarker = {
  markerKey: string; // the date string (YYYY-MM-DD)
  markerName: string;
  markerValue: number;
  markerObsDate: string;
  gaugeData: GaugeData;
  gaugeConfig: GaugeConfig;
  clickPosition?: { x: number; y: number };
};

type DashboardToggleContextType = {
  // --- Dashboard Observation Reference Date ---
  // --- Active ref date marker modal ---
  activeRefDateMarker: ActiveRefDateMarker | null;
  setActiveRefDateMarker: (marker: ActiveRefDateMarker | null) => void;

  // --- Observation date ---
  obsRefDate: string | null; // ISO date string — sent as `obsRefDate` to the cache
  setObsRefDate: (date: string | null) => void;

  // --- Dashboard variant ---
  dashboardVariant: string | null;
  setDashboardVariant: (variant: string | null) => void;

  // When true, gauges that successfully applied the dashboard variant show a colored border
  showVariantHighlight: boolean;
  setShowVariantHighlight: (value: boolean) => void;

  // Union set of all variants registered by mounted gauges
  availableVariants: string[];

  // Registration — each GaugeChart calls these on mount/unmount
  registerGaugeVariants: (displayId: string, variants: string[]) => void;
  unregisterGaugeVariants: (displayId: string) => void;

  // --- Sort ---
  sortType: SortType;
  sortDirection: SortDirection;
  onSortTypeChange: (type: SortType) => void;
  onSortDirectionChange: (direction: SortDirection) => void;

  // --- Preview marker ---
  previewMarkerValue: number | null;
  setPreviewMarkerValue: (value: number | null) => void;

  // --- Gauge zoom ---
  gaugeZoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  saveZoom: () => void;
  isZoomSaved: boolean;

  // --- Landing page ---
  isLandingPage: boolean;
  setIsLandingPage: (value: boolean) => void;

  // --- Reset ---
  // Incremented each time a full reset is requested; gauges listen on this to restore defaults
  resetTrigger: number;
  triggerReset: () => void;
};

const DashboardToggleContext = createContext<DashboardToggleContextType>({
  activeRefDateMarker: null,
  setActiveRefDateMarker: () => {},
  obsRefDate: null,
  setObsRefDate: () => {},
  dashboardVariant: null,
  setDashboardVariant: () => {},
  showVariantHighlight: false,
  setShowVariantHighlight: () => {},
  availableVariants: [],
  registerGaugeVariants: () => {},
  unregisterGaugeVariants: () => {},
  sortType: SortType.DEFAULT,
  sortDirection: SortDirection.ASC,
  onSortTypeChange: () => {},
  onSortDirectionChange: () => {},
  previewMarkerValue: null,
  setPreviewMarkerValue: () => {},
  gaugeZoom: 1,
  zoomIn: () => {},
  zoomOut: () => {},
  saveZoom: () => {},
  isZoomSaved: true,
  isLandingPage: false,
  setIsLandingPage: () => {},
  resetTrigger: 0,
  triggerReset: () => {},
});

const LANDING_ZOOM = 0.6;

export function DashboardToggleProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeRefDateMarker, setActiveRefDateMarker] =
    useState<ActiveRefDateMarker | null>(null);
  const [previewMarkerValue, setPreviewMarkerValue] = useState<number | null>(
    null,
  );
  const [obsRefDate, setObsRefDate] = useState<string | null>(null);
  const [dashboardVariant, _setDashboardVariant] = useState<string | null>(
    null,
  );
  const [showVariantHighlight, setShowVariantHighlight] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isLandingPage, setIsLandingPage] = useState(false);
  const [_gaugeZoom, setGaugeZoom] = useState(1);
  const [savedZoom, setSavedZoom] = useState(1);

  useEffect(() => {
    const saved = localStorage.getItem("gaugeZoom");
    if (saved) {
      const parsed = parseFloat(saved);
      if (!Number.isNaN(parsed) && parsed >= 0.4 && parsed <= 1) {
        setGaugeZoom(parsed);
        setSavedZoom(parsed);
      }
    }
  }, []);

  const gaugeZoom = isLandingPage ? LANDING_ZOOM : _gaugeZoom;

  const zoomIn = useCallback(() => {
    setGaugeZoom((z) => Math.min(Math.round((z + 0.1) * 10) / 10, 1));
  }, []);

  const zoomOut = useCallback(() => {
    setGaugeZoom((z) => Math.max(Math.round((z - 0.1) * 10) / 10, 0.4));
  }, []);

  const saveZoom = useCallback(() => {
    localStorage.setItem("gaugeZoom", String(_gaugeZoom));
    setSavedZoom(_gaugeZoom);
  }, [_gaugeZoom]);

  const isZoomSaved = Math.abs(_gaugeZoom - savedZoom) < 0.001;


  // --- Sort ---
  const [sortType, setSortType] = useState<SortType>(SortType.DEFAULT);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASC,
  );

  const onSortTypeChange = useCallback((type: SortType) => {
    setSortType(type);
    if (type === SortType.DEFAULT || type === SortType.ALPHABETICAL) {
      setSortDirection(SortDirection.ASC);
    } else {
      setSortDirection(SortDirection.DESC);
    }
  }, []);

  const onSortDirectionChange = useCallback((direction: SortDirection) => {
    setSortDirection(direction);
  }, []);

  const setDashboardVariant = useCallback((variant: string | null) => {
    _setDashboardVariant(variant);
    if (variant === null) setShowVariantHighlight(false);
  }, []);

  const triggerReset = useCallback(() => {
    _setDashboardVariant(null);
    setShowVariantHighlight(false);
    setObsRefDate(null);
    setPreviewMarkerValue(null);
    setResetTrigger((n) => n + 1);
    setSortType(SortType.DEFAULT);
    setSortDirection(SortDirection.ASC);
  }, []);

  // Maps displayId → that gauge's supported variants
  const [gaugeVariantMap, setGaugeVariantMap] = useState<
    Record<string, string[]>
  >({});

  const registerGaugeVariants = useCallback(
    (displayId: string, variants: string[]) => {
      setGaugeVariantMap((prev) => {
        // Same variants as before: keep the old map so nothing re-renders
        const current = prev[displayId];
        if (current && current.length === variants.length && current.every((v, i) => v === variants[i])) {
          return prev;
        }
        return { ...prev, [displayId]: variants };
      });
    },
    [],
  );

  const unregisterGaugeVariants = useCallback((displayId: string) => {
    setGaugeVariantMap((prev) => {
      const next = { ...prev };
      delete next[displayId];
      return next;
    });
  }, []);

  // Union of all variants across all registered gauges, preserving SUPPORTED_DISPLAY_VARIANTS order
  const availableVariants = useMemo(
    () =>
      [...new Set(Object.values(gaugeVariantMap).flat())].sort((a, b) =>
        a.localeCompare(b),
      ),
    [gaugeVariantMap],
  );

  // Memoized so consumers (every gauge) only re-render when a value actually
  // changes, not every time this provider re-renders.
  const value = useMemo(
    () => ({
      activeRefDateMarker,
      setActiveRefDateMarker,
      obsRefDate,
      setObsRefDate,
      dashboardVariant,
      setDashboardVariant,
      showVariantHighlight,
      setShowVariantHighlight,
      availableVariants,
      registerGaugeVariants,
      unregisterGaugeVariants,
      sortType,
      sortDirection,
      onSortTypeChange,
      onSortDirectionChange,
      previewMarkerValue,
      setPreviewMarkerValue,
      gaugeZoom,
      zoomIn,
      zoomOut,
      saveZoom,
      isZoomSaved,
      isLandingPage,
      setIsLandingPage,
      resetTrigger,
      triggerReset,
    }),
    [
      activeRefDateMarker,
      obsRefDate,
      dashboardVariant,
      setDashboardVariant,
      showVariantHighlight,
      availableVariants,
      registerGaugeVariants,
      unregisterGaugeVariants,
      sortType,
      sortDirection,
      onSortTypeChange,
      onSortDirectionChange,
      previewMarkerValue,
      gaugeZoom,
      zoomIn,
      zoomOut,
      saveZoom,
      isZoomSaved,
      isLandingPage,
      resetTrigger,
      triggerReset,
    ],
  );

  return (
    <DashboardToggleContext.Provider value={value}>
      {children}
    </DashboardToggleContext.Provider>
  );
}

export const useDashboardToggle = () => useContext(DashboardToggleContext);
